import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getInsightContentTypeDisplayText } from 'components/insightContent/utils/utils'
import { Theme } from 'features/ui/types'
import { WidgetId } from 'features/types'
import { useDisplayTheme } from 'hooks/useUiState'
// @ts-ignore
import { Tooltip, Dialog } from '@netbasequid/core'
import AccordionSectionType from 'components/insightContent/enum/AccordionSectionType'
import { TopInfoBox, TopInfoRight } from 'components/insightContent/visualization/InfoBox/index.css'
import { CountTypeStrings } from 'enums/CountType'
import { InsightContentSettingType } from 'components/insightContent/types'
import AccordionGroup from 'components/insightContent/visualization/AccordionGroup'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import { MetricTimeUnitStrings } from 'enums/MetricTimeUnit'
import { AutomationId } from 'utils/automationUtil'
import { sendTypedMessage } from 'utils/iframeUtil'
import { toExternal } from 'vo/util'
import InsightDialog from 'components/insightContent/visualization/InsightDialog'
import GoToAnalysisButton from 'components/insightContent/visualization/GoToAnalysisButton'
import { InsightObjective } from '../../types'
import useAccordionSummaryContents from './hooks'
import {
  StyledVersus,
  StyledSubtitleContainer,
  StyledTopicPivotDescription,
  StyledTopicPivotName
} from './index.css'

const DIALOG_CONTENT_HEIGHT = '460px'

const InsightModal = ({
  widgetId,
  isAudienceQuery = false,
  open,
  title,
  countType,
  selection,
  onClose,
  detailSetting,
  timeUnits,
  forceNotScale,
  widgetSettingWithSelection
}: {
  widgetId: WidgetId
  isAudienceQuery?: boolean
  open: boolean
  title: string | undefined
  countType: CountTypeStrings
  selection: InsightObjective | undefined
  onClose: () => void
  detailSetting: InsightContentSettingType | undefined
  timeUnits?: MetricTimeUnitStrings[]
  forceNotScale?: boolean
  widgetSettingWithSelection?: any
}) => {
  const theme = useDisplayTheme() as Theme
  const [displayTopicTooltip, setDisplayTopicTooltip] = useState(false)
  const [displayPivotTooltip, setDisplayPivotTooltip] = useState(false)
  const accordionSummaryContents = useAccordionSummaryContents({
    widgetId,
    isAudienceQuery,
    detailSetting,
    viewOption: detailSetting?.viewOption,
    pivot: selection ? selection.pivot : undefined
  })
  const { t } = useTranslation()

  if (!selection) {
    return null
  }

  const checkTopicContent = (
    setTooltipTriggerRef: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  ) => (StyledNameContainerRef: HTMLDivElement) => {
    if (!StyledNameContainerRef) {
      return
    }

    setDisplayTopicTooltip(
      StyledNameContainerRef.scrollHeight! > StyledNameContainerRef.clientHeight!
    )
    setTooltipTriggerRef(StyledNameContainerRef)
  }

  const checkPivotContent = (
    setTooltipTriggerRef: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  ) => (StyledNameContainerRef: HTMLDivElement) => {
    if (!StyledNameContainerRef) {
      return
    }

    setDisplayPivotTooltip(
      StyledNameContainerRef.scrollHeight! > StyledNameContainerRef.clientHeight!
    )
    setTooltipTriggerRef(StyledNameContainerRef)
  }

  function goToAnalysis() {
    sendTypedMessage('goToAnalysis', toExternal(widgetSettingWithSelection))
  }

  const resolveVersus = () =>
    displayTopicTooltip ? (
      <>
        <StyledVersus mode={theme}>{t('and')}</StyledVersus>&nbsp;
      </>
    ) : (
      <>
        &nbsp;<StyledVersus mode={theme}>{t('and')}</StyledVersus>&nbsp;
      </>
    )

  const { pivot, insight } = selection
  return (
    <InsightDialog open={open} onClose={onClose}>
      <Dialog.DialogTitle>{title}</Dialog.DialogTitle>
      <TopInfoBox mode={theme}>
        <StyledSubtitleContainer>
          <StyledTopicPivotDescription>
            <Tooltip
              mode={theme}
              enabled={displayTopicTooltip}
              callbackRefCreator={checkTopicContent}
              content={insight.name}>
              <StyledTopicPivotName
                testId={AutomationId.insightModal.selectedTopic}
                hasMaxWidth={pivot !== undefined}
                mode={theme}>
                {insight.name}
              </StyledTopicPivotName>
            </Tooltip>
            {pivot ? resolveVersus() : ''}
            {pivot ? (
              <Tooltip
                mode={theme}
                enabled={displayPivotTooltip}
                callbackRefCreator={checkPivotContent}
                content={pivot.name}>
                <StyledTopicPivotName
                  testId={AutomationId.insightModal.selectedPivot}
                  hasMaxWidth
                  mode={theme}>
                  {pivot.name}
                </StyledTopicPivotName>
              </Tooltip>
            ) : (
              ''
            )}
          </StyledTopicPivotDescription>
        </StyledSubtitleContainer>
        <TopInfoRight>
          <GoToAnalysisButton onClick={() => goToAnalysis()} />
        </TopInfoRight>
      </TopInfoBox>
      <Dialog.DialogContent height={DIALOG_CONTENT_HEIGHT}>
        <AccordionGroup
          timeUnits={timeUnits}
          forceNotScale={forceNotScale}
          countType={countType}
          insightContentsConfig={[
            {
              accordionTitle: getInsightContentTypeDisplayText(AccordionSectionType.SUMMARY),
              accordionContents: accordionSummaryContents
            },
            {
              accordionTitle: getInsightContentTypeDisplayText(AccordionSectionType.SOUND_BITE),
              accordionContents: [
                {
                  insightContentType: InsightContentType.SOUND_BITE,
                  detailSetting
                }
              ]
            }
          ]}
        />
      </Dialog.DialogContent>
      <Dialog.DialogActions />
    </InsightDialog>
  )
}

export default InsightModal
